import dayjs from 'dayjs'
import {
  BoosterCodeExpiry,
  BoosterEnrollmentPeriod,
  BoosterMilestoneApiItem,
  BoosterMilestoneApiRewardType,
  BoosterMilestoneCompletion,
  BoosterMilestoneCreatePayload,
  BoosterMilestoneDetail,
  BoosterMilestoneFormValues,
  BoosterProgramApiItem,
  BoosterProgramApiTargetUserType,
  BoosterProgramCreatePayload,
  BoosterProgramDetail,
  BoosterProgramDetailStats,
  BoosterProgramDetailStatsResponse,
  BoosterProgramEnrolledUser,
  BoosterProgramEnrolledUserApiItem,
  BoosterProgramFormValues,
  BoosterProgramItem,
  BoosterProgramStatus,
  BoosterProgramTargetUser,
  BoosterRewardType,
} from 'Modules'

import {
  BOOSTER_PROGRAM_STATUS_FROM_CODE,
  BOOSTER_PROGRAM_STATUS_TO_CODE,
  BOOSTER_PROGRAM_TARGET_USER_FROM_API,
  BOOSTER_PROGRAM_TARGET_USER_TO_API,
} from 'constants/booster-program'

const API_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss'

/**
 * Normalize the API enrollment period into a `{ value, unit }` pair.
 * Tolerates legacy records where `enrollmentPeriod` was a bare number (months).
 */
const normalizeEnrollmentPeriod = (
  raw: BoosterEnrollmentPeriod | number | undefined | null
): BoosterEnrollmentPeriod => {
  if (typeof raw === 'number') return { value: raw, unit: 'months' }
  return { value: raw?.value ?? 0, unit: raw?.unit ?? 'months' }
}

const DEFAULT_CODE_EXPIRY: BoosterCodeExpiry = '30_days'

const CODE_EXPIRY_DURATION: Record<
  BoosterCodeExpiry,
  { value: number; unit: dayjs.ManipulateType }
> = {
  '5_minutes': { value: 5, unit: 'minute' },
  '10_minutes': { value: 10, unit: 'minute' },
  '1_hour': { value: 1, unit: 'hour' },
  '15_days': { value: 15, unit: 'day' },
  '30_days': { value: 30, unit: 'day' },
  '60_days': { value: 60, unit: 'day' },
  '90_days': { value: 90, unit: 'day' },
}

const FE_TO_BE_REWARD_TYPE: Record<BoosterRewardType, BoosterMilestoneApiRewardType> = {
  DISCOUNT_CODE: 'discount_value',
  APR_BOOSTER_CODE: 'boost_apr_value',
  CASHBACK_TOKEN_CODE: 'cashback_rate',
  PTO_BONUS_CODE: 'bonus_value',
}

const BE_TO_FE_REWARD_TYPE: Partial<Record<BoosterMilestoneApiRewardType, BoosterRewardType>> = {
  discount_value: 'DISCOUNT_CODE',
  boost_apr_value: 'APR_BOOSTER_CODE',
  cashback_rate: 'CASHBACK_TOKEN_CODE',
  bonus_value: 'PTO_BONUS_CODE',
  pto_sold_out: 'PTO_BONUS_CODE',
}

const CODE_EXPIRY_VALUES = new Set(Object.keys(CODE_EXPIRY_DURATION))

/**
 * Resolve the BE codeExpiry into a form bucket.
 * - New BE format: codeExpiry is already an enum string (e.g. '30_days') — use as-is.
 * - Legacy format: codeExpiry is an absolute ISO date; diff against the creation
 *   timestamp and snap to the nearest bucket by total minutes. Lossy when the value
 *   doesn't fall on a bucket boundary — falls back to the closest one, or '30_days'
 *   when no reference date is available.
 */
const inferCodeExpiry = (codeExpiry?: string, refIso?: string): BoosterCodeExpiry => {
  if (!codeExpiry) return DEFAULT_CODE_EXPIRY
  if (CODE_EXPIRY_VALUES.has(codeExpiry)) return codeExpiry as BoosterCodeExpiry
  const ref = refIso ? dayjs(refIso) : dayjs()
  const diffMinutes = dayjs(codeExpiry).diff(ref, 'minute', true)
  let best: BoosterCodeExpiry = DEFAULT_CODE_EXPIRY
  let bestDelta = Infinity
  for (const [key, duration] of Object.entries(CODE_EXPIRY_DURATION) as [
    BoosterCodeExpiry,
    { value: number; unit: dayjs.ManipulateType },
  ][]) {
    const bucketMinutes = ref.add(duration.value, duration.unit).diff(ref, 'minute', true)
    const delta = Math.abs(bucketMinutes - diffMinutes)
    if (delta < bestDelta) {
      bestDelta = delta
      best = key
    }
  }
  return best
}

const mapApiMilestoneRewardToFormValues = (
  reward: BoosterMilestoneApiItem['reward'],
  minPurchased?: number
): Pick<
  BoosterMilestoneFormValues,
  | 'rewardType'
  | 'discountValue'
  | 'boostAprValue'
  | 'cashbackRate'
  | 'bonusBuy'
  | 'bonusGet'
  | 'tokenType'
  | 'ptoSoldOut'
> => {
  const rewardType = BE_TO_FE_REWARD_TYPE[reward.type] ?? 'DISCOUNT_CODE'
  const params = reward.params ?? {}
  const base = { rewardType }
  switch (rewardType) {
    case 'DISCOUNT_CODE':
      return { ...base, discountValue: params.percent ?? 0 }
    case 'APR_BOOSTER_CODE':
      return { ...base, boostAprValue: params.percent ?? 0 }
    case 'CASHBACK_TOKEN_CODE':
      return {
        ...base,
        cashbackRate: params.percent ?? 0,
        tokenType: params.tokenType ? params.tokenType.toUpperCase() : 'MEY',
        ptoSoldOut: params.ptoSoldOut ?? false,
      }
    case 'PTO_BONUS_CODE': {
      // "Buy X get Y": bonusBuy = minPurchased (X), bonusGet = bonusQuantity (Y).
      // `minPurchased` now lives in reward.params; fall back to the legacy
      // top-level field for older records.
      return {
        ...base,
        bonusBuy: params.minPurchased ?? minPurchased ?? 0,
        bonusGet: params.bonusQuantity ?? 0,
        ptoSoldOut: params.ptoSoldOut ?? false,
      }
    }
  }
}

export const mapApiToMilestoneFormValues = (
  api: BoosterMilestoneApiItem
): BoosterMilestoneFormValues => ({
  id: api._id,
  threshold: api.conditions?.[0]?.params?.value ?? 1,
  totalSupply: api.totalSupply ?? 0,
  codeExpiry: inferCodeExpiry(api.codeExpiry, api.createdAt),
  linkedPtos: (api.linkedPTOs ?? []).map((p) => p._id),
  ...mapApiMilestoneRewardToFormValues(api.reward, api.minPurchased),
})

export const mapApiToMilestoneDetail = (
  api: BoosterMilestoneApiItem,
  index: number
): BoosterMilestoneDetail => {
  const form = mapApiToMilestoneFormValues(api)
  const totalSupply = api.totalSupply ?? 0
  const issued = api.issuedCodes ?? 0
  return {
    ...form,
    id: api._id,
    index,
    totalSupply,
    codeExpiry: form.codeExpiry ?? '30_days',
    issued,
    remaining: Math.max(totalSupply - issued, 0),
    linkedPtos: (api.linkedPTOs ?? []).map((p) => ({
      ptoId: p._id,
      ptoName: p.fullName ?? '',
      sellingStatus: p.soldOutStatus === 1 ? 'SOLD_OUT' : 'ON_SALE',
      pending: p.pending,
      claimed: p.claimed,
      applied: p.applied,
    })),
  }
}

export const mapApiToEnrolledUser = (
  api: BoosterProgramEnrolledUserApiItem,
  index: number
): BoosterProgramEnrolledUser => {
  const wallet = api.walletAddress ?? ''
  const statusLower = (api.status ?? '').toLowerCase()
  const status: BoosterProgramEnrolledUser['status'] =
    statusLower === 'expired' ? 'EXPIRED' : 'ACTIVE'
  const milestonesReached = (api.milestoneProgress ?? []).reduce<number[]>((acc, m, i) => {
    if ((m.status ?? '').toLowerCase() === 'pass') acc.push(i)
    return acc
  }, [])
  return {
    // BE doesn't return a stable id — use walletAddress (unique per program) with
    // index fallback in case of duplicates.
    id: wallet || `enrolled-${index}`,
    walletAddress: wallet,
    enrolledOn: api.enrolledOn ?? '',
    period: api.period ?? '',
    milestonesReached,
    status,
  }
}

const STATUS_MESSAGES: Record<BoosterProgramStatus, string> = {
  DRAFT: 'This Booster Program is in draft.',
  SCHEDULED: 'This Booster Program is scheduled to start.',
  ACTIVE:
    'The Booster Program is currently active. All referred users can earn promo codes when they reach milestone thresholds.',
  PAUSED: 'This Booster Program is paused. No new codes are being issued.',
  EXPIRED: 'This Booster Program has expired.',
}

export const mapApiToBoosterProgramItem = (api: BoosterProgramApiItem): BoosterProgramItem => ({
  id: api._id,
  title: api.title,
  createdAt: api.createdAt,
  targetUsers: BOOSTER_PROGRAM_TARGET_USER_FROM_API[api.targetUser?.type] ?? 'ALL',
  startDate: api.period?.startDate ?? '',
  endDate: api.period?.endDate ?? '',
  enrolledUsers: api.enrolledUsers ?? 0,
  milestones:
    api.totalActiveMilestones ?? (Array.isArray(api.milestones) ? api.milestones.length : 0),
  status: BOOSTER_PROGRAM_STATUS_FROM_CODE[api.status] ?? 'DRAFT',
})

export const mapStatusToApiCode = (status?: BoosterProgramStatus): number | undefined => {
  if (!status) return undefined
  return BOOSTER_PROGRAM_STATUS_TO_CODE[status]
}

export const mapTargetUsersToApi = (
  targetUsers?: BoosterProgramTargetUser
): BoosterProgramApiTargetUserType | undefined => {
  if (!targetUsers) return undefined
  return BOOSTER_PROGRAM_TARGET_USER_TO_API[targetUsers]
}

/**
 * Format the start date for BE, preserving the user-selected time (down to the
 * minute). BE requires startDate strictly in the future, so when the picked
 * datetime is already in the past, nudge it to `now + 2s`.
 */
const formatStartDate = (start: dayjs.ConfigType): string => {
  // Seconds are always pinned to 00 — the period picker only goes down to the minute.
  const startTime = dayjs(start).second(0)
  const now = dayjs()
  if (!startTime.isAfter(now)) {
    // Start is in the past/now: bump to the next minute so it stays in the future with 00 seconds.
    return now.add(1, 'minute').second(0).format(API_DATE_FORMAT)
  }
  return startTime.format(API_DATE_FORMAT)
}

export const mapFormToBoosterProgramCreatePayload = (
  values: BoosterProgramFormValues
): BoosterProgramCreatePayload => {
  const [start, end] = values.period ?? []
  return {
    title: values.title ?? '',
    period: {
      startDate: start ? formatStartDate(start) : '',
      endDate: end ? dayjs(end).second(0).format(API_DATE_FORMAT) : '',
    },
    descriptions: values.description ?? '',
    termConditions: {
      content: values.terms ?? '',
      conditions: [],
    },
    enrollmentPeriod: {
      value: values.enrollmentPeriodValue ?? 0,
      unit: values.enrollmentPeriodUnit ?? 'months',
    },
    targetUser: {
      type: values.targetUsers
        ? BOOSTER_PROGRAM_TARGET_USER_TO_API[values.targetUsers]
        : 'all_users',
    },
    participantLimit: {
      type: values.participantLimitType === 'LIMITED' ? 'limit' : 'unlimit',
      numberLimit: values.participantLimit ?? undefined,
      startFillNumber: values.startingFillNumber ?? 0,
      increaseBy: {
        percent: values.increaseByPercent ?? 0,
        type: values.increaseEveryUnit ?? 'days',
        value: values.increaseEvery ?? 0,
      },
    },
  }
}

export const mapMilestoneFormToCreatePayload = (
  m: BoosterMilestoneFormValues
): BoosterMilestoneCreatePayload => {
  const reward: BoosterMilestoneCreatePayload['reward'] = {
    type: FE_TO_BE_REWARD_TYPE[m.rewardType],
    params: {},
  }

  switch (m.rewardType) {
    case 'DISCOUNT_CODE':
      reward.params.percent = m.discountValue ?? 0
      break
    case 'APR_BOOSTER_CODE':
      reward.params.percent = m.boostAprValue ?? 0
      break
    case 'CASHBACK_TOKEN_CODE':
      reward.params.percent = m.cashbackRate ?? 0
      reward.params.tokenType = (m.tokenType ?? 'MEY').toLowerCase() as 'mey' | 'usdc'
      reward.params.ptoSoldOut = m.ptoSoldOut ?? false
      break
    case 'PTO_BONUS_CODE': {
      // Bonus Code form is "Buy X get Y" — `reward.params.minPurchased = X` and
      // `reward.params.bonusQuantity = Y` (the bonus PTO count, sent as-is).
      reward.params.minPurchased = m.bonusBuy ?? 0
      reward.params.bonusQuantity = m.bonusGet ?? 0
      reward.params.tokenType = (m.tokenType ?? 'MEY').toLowerCase() as 'mey' | 'usdc'
      reward.params.ptoSoldOut = m.ptoSoldOut ?? false
      break
    }
  }

  return {
    id: m.id,
    conditions: [{ action: 'purchase_quantity', params: { value: m.threshold ?? 1 } }],
    reward,
    totalSupply: m.totalSupply ?? 1,
    // BE expects the enum string (e.g. '30_days') and computes the actual expiry itself.
    codeExpiry: m.codeExpiry ?? DEFAULT_CODE_EXPIRY,
    linkedPTOs: m.linkedPtos ?? [],
  }
}

export const mapApiItemToFormValues = (api: BoosterProgramApiItem): BoosterProgramFormValues => {
  const enrollment = normalizeEnrollmentPeriod(api.enrollmentPeriod)
  return {
    title: api.title ?? '',
    period: [
      api.period?.startDate ? (dayjs(api.period.startDate) as any) : (undefined as any),
      api.period?.endDate ? (dayjs(api.period.endDate) as any) : (undefined as any),
    ],
    description: api.descriptions ?? '',
    terms: api.termConditions?.content ?? '',
    enrollmentPeriodValue: enrollment.value || undefined,
    enrollmentPeriodUnit: enrollment.unit,
    targetUsers: BOOSTER_PROGRAM_TARGET_USER_FROM_API[api.targetUser?.type] ?? 'ALL',
    whitelistFileName: api.targetUser?.params?.fileName ?? undefined,
    participantLimitType: api.participantLimit?.type === 'limit' ? 'LIMITED' : 'UNLIMITED',
    participantLimit: api.participantLimit?.numberLimit,
    startingFillNumber: api.participantLimit?.startFillNumber,
    increaseByPercent: api.participantLimit?.increaseBy?.percent ?? 0,
    increaseEvery: api.participantLimit?.increaseBy?.value ?? 0,
    increaseEveryUnit: api.participantLimit?.increaseBy?.type ?? 'days',
    milestones: (api.milestones ?? []).map(mapApiToMilestoneFormValues),
  }
}

export const mapApiToBoosterProgramDetail = (api: BoosterProgramApiItem): BoosterProgramDetail => {
  const status = BOOSTER_PROGRAM_STATUS_FROM_CODE[api.status] ?? 'DRAFT'
  const enrollment = normalizeEnrollmentPeriod(api.enrollmentPeriod)

  return {
    id: api._id,
    title: api.title,
    status,
    isActive: status === 'ACTIVE',
    statusMessage: STATUS_MESSAGES[status],
    period: {
      start: api.period?.startDate ?? '',
      end: api.period?.endDate ?? '',
    },
    description: api.descriptions ?? '',
    terms: api.termConditions?.content ?? '',
    enrollmentPeriodValue: enrollment.value,
    enrollmentPeriodUnit: enrollment.unit,
    targetUsers: BOOSTER_PROGRAM_TARGET_USER_FROM_API[api.targetUser?.type] ?? 'ALL',
    whitelistFileName: api.targetUser?.params?.fileName ?? undefined,
    participantLimitType: api.participantLimit?.type === 'limit' ? 'LIMITED' : 'UNLIMITED',
    participantLimit: api.participantLimit?.numberLimit,
    startingFillNumber: api.participantLimit?.startFillNumber,
    increaseByPercent: api.participantLimit?.increaseBy?.percent,
    increaseEvery: api.participantLimit?.increaseBy?.value,
    increaseEveryUnit: api.participantLimit?.increaseBy?.type,
    milestones: (api.milestones ?? []).map(mapApiToMilestoneDetail),
  }
}

export const mapApiToProgramDetailStats = (
  api: BoosterProgramDetailStatsResponse
): { stats: BoosterProgramDetailStats; milestoneCompletion: BoosterMilestoneCompletion[] } => ({
  stats: {
    totalEnrolled: api.totalEnrolledUsers ?? 0,
    activeEnrolledUsers: api.totalActiveEnrolledUsers ?? 0,
    codesIssued: api.totalCodesIssued ?? 0,
    codesApplied: api.totalCodesApplied ?? 0,
  },
  milestoneCompletion: (api.milestoneCompletion ?? []).map((m, i) => ({
    label: `M${i + 1}`,
    completed: m.totalEnrolledPass ?? 0,
    total: m.totalEnrolled ?? 0,
  })),
})
